import useMutateItem from "../useMutateItem";
import useFetchItem from "../useFetchItem";
import HttpService from "../httpServices";
import { routes } from "../apiroutes";
import { showSuccessAlert, showErrorAlert } from "../../utils/alert";
import { ErrorHandler } from "../errorHandler";

const httpService = new HttpService();

// Company Management
export const useCreateCompany = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.company()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Company created successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    createCompanyData: data,
    createCompanyError: ErrorHandler(error),
    createCompanyIsLoading: isPending,
    createCompanyPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useGetCompanyInfo = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['companyInfo'],
    queryFn: () => httpService.getData(routes.companyInfo()),
    enabled: options.enabled !== false, // Default to true if not specified
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    companyInfo: data,
    companyInfoError: ErrorHandler(error),
    companyInfoIsLoading: isLoading,
    refetchCompanyInfo: refetch,
  };
};

// Branch Management
export const useCreateBranch = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.branches()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Branch created successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    createBranchData: data,
    createBranchError: ErrorHandler(error),
    createBranchIsLoading: isPending,
    createBranchPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useGetBranches = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['branches'],
    queryFn: () => {
      const url = routes.branches();
  
      return httpService.getData(url);
    },
    enabled: options.enabled !== false, // Default to true if not specified
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('❌ Branches API Error:', error);
      showErrorAlert(error);
    },
  });



  return {
    branches: data?.data || data,
    branchesError: ErrorHandler(error),
    branchesIsLoading: isLoading,
    refetchBranches: refetch,
  };
};

// Alias for BranchFilter component compatibility
export const useFetchBranches = useGetBranches;

export const useUpdateBranch = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ branchId, payload }) =>
      httpService.putData(payload, routes.branchById(branchId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Branch updated successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    updateBranchData: data,
    updateBranchError: ErrorHandler(error),
    updateBranchIsLoading: isPending,
    updateBranchPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useDeleteBranch = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (branchId) =>
      httpService.deleteData(routes.branchById(branchId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Branch deleted successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    deleteBranchData: data,
    deleteBranchError: ErrorHandler(error),
    deleteBranchIsLoading: isPending,
    deleteBranchPayload: (requestPayload) => mutate(requestPayload),
  };
};

// Branch Status Management
export const useActivateBranch = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (branchId) =>
      httpService.putData({ action: 'activate' }, routes.branchActivate(branchId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Branch activated successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    activateBranchData: data,
    activateBranchError: ErrorHandler(error),
    activateBranchIsLoading: isPending,
    activateBranchPayload: (branchId) => mutate(branchId),
  };
};

export const useDeactivateBranch = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (branchId) =>
      httpService.putData({ action: 'deactivate' }, routes.branchDeactivate(branchId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Branch deactivated successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    deactivateBranchData: data,
    deactivateBranchError: ErrorHandler(error),
    deactivateBranchIsLoading: isPending,
    deactivateBranchPayload: (branchId) => mutate(branchId),
  };
};

// User Management
export const useCreateBranchUser = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ branchId, payload }) =>
      httpService.postData(payload, routes.branchUsers(branchId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("User created successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    createBranchUserData: data,
    createBranchUserError: ErrorHandler(error),
    createBranchUserIsLoading: isPending,
    createBranchUserPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useGetAllUsers = (filters = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['allUsers', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      const queryString = params.toString();
      return httpService.getData(`${routes.allUsers()}${queryString ? `?${queryString}` : ''}`);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    allUsers: data,
    allUsersError: ErrorHandler(error),
    allUsersIsLoading: isLoading,
    refetchAllUsers: refetch,
  };
};

export const useGetBranchUsers = (branchId) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['branchUsers', branchId],
    queryFn: () => httpService.getData(routes.branchUsers(branchId)),
    enabled: !!branchId,
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    branchUsers: data,
    branchUsersError: ErrorHandler(error),
    branchUsersIsLoading: isLoading,
    refetchBranchUsers: refetch,
  };
};

export const useTransferUser = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ userId, payload }) =>
      httpService.putData(payload, routes.userTransfer(userId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("User transferred successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    transferUserData: data,
    transferUserError: ErrorHandler(error),
    transferUserIsLoading: isPending,
    transferUserPayload: (requestPayload) => mutate(requestPayload),
  };
};

// Admin Cross-Branch Data Access
export const useGetAdminEmployees = (filters = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['adminEmployees', filters],
    queryFn: () => {
      const params = new URLSearchParams(filters);
      const url = `${routes.adminEmployees()}?${params}`;
  
      return httpService.getData(url);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('❌ Admin Employees API Error:', error);
      showErrorAlert(error);
    },
  });

  // Debug logging


  return {
    adminEmployees: data,
    adminEmployeesError: ErrorHandler(error),
    adminEmployeesIsLoading: isLoading,
    refetchAdminEmployees: refetch,
  };
};

export const useGetAdminAttendanceReports = (filters = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['adminAttendanceReports', filters],
    queryFn: () => {
      const params = new URLSearchParams(filters);
      return httpService.getData(`${routes.adminAttendanceReports()}?${params}`);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    adminAttendanceReports: data,
    adminAttendanceReportsError: ErrorHandler(error),
    adminAttendanceReportsIsLoading: isLoading,
    refetchAdminAttendanceReports: refetch,
  };
};

export const useGetCompanyAnalytics = (filters = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['companyAnalytics', filters],
    queryFn: () => {
      const params = new URLSearchParams(filters);
      return httpService.getData(`${routes.adminCompanyAnalytics()}?${params}`);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    companyAnalytics: data,
    companyAnalyticsError: ErrorHandler(error),
    companyAnalyticsIsLoading: isLoading,
    refetchCompanyAnalytics: refetch,
  };
};

// User Management - Status, Delete, Details
export const useUpdateUserStatus = () => {
  return useMutateItem({
    mutationFn: (data) => httpService.putData({ 
      status: data.status,
      action: data.status === 'active' ? 'activate' : 'deactivate'
    }, routes.userStatus(data.user_id)),
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });
};

export const useDeleteUser = () => {
  return useMutateItem({
    mutationFn: (userId) => httpService.deleteData(routes.deleteUser(userId), { action: 'delete' }),
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });
};

export const useGetUserDetails = (userId) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['userDetails', userId],
    queryFn: () => httpService.getData(routes.getUserDetails(userId)),
    enabled: !!userId,
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    userDetails: data,
    userDetailsError: ErrorHandler(error),
    userDetailsIsLoading: isLoading,
    refetchUserDetails: refetch,
  };
};

// Plan Information
export const useGetPlanInfo = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['planInfo', options.enabled],
    queryFn: () => httpService.getData(routes.getPlanInfo()),
    enabled: options.enabled !== false, // Default to true if not specified
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    planInfo: data,
    planInfoError: ErrorHandler(error),
    planInfoIsLoading: isLoading,
    refetchPlanInfo: refetch,
  };
};

 